import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RejectedValue, ErrorResponse } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { RecipientTreeSourceEmployee } from 'components/RecipientTree/recipient-tree-interfaces';
import {
    AutomatedMessageModel,
    AutomatedMessageRequest,
    AutomatedMessageSliceModel,
    ChangeAutomatedMessageModel,
    SetTenantOptOutRequest
} from './automated-message-interfaces';
import { API_BASE_URL as baseUri } from 'config/env';

export const GetActiveAutomatedMessages = createAsyncThunk<AutomatedMessageModel[], void, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/GetActiveAutomatedMessages',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/getAllActive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AutomatedMessageModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveAutomatedMessages = createAsyncThunk<AutomatedMessageModel[], void, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/GetInactiveAutomatedMessages',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/getAllInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AutomatedMessageModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAutomatedMessage = createAsyncThunk<AutomatedMessageModel, string, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/GetAutomatedMessage',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/get?automatedMessageId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as AutomatedMessageModel;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateAutomatedMessage = createAsyncThunk<ChangeAutomatedMessageModel, AutomatedMessageRequest, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/CreateAutomatedMessage',
    async (request: AutomatedMessageRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ChangeAutomatedMessageModel;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateAutomatedMessage = createAsyncThunk<ChangeAutomatedMessageModel, AutomatedMessageRequest, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/UpdateAutomatedMessage',
    async (request: AutomatedMessageRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as ChangeAutomatedMessageModel;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateAutomatedMessage = createAsyncThunk<ChangeAutomatedMessageModel, string, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/DeactivateAutomatedMessage',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/deactivate?automatedMessageId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as ChangeAutomatedMessageModel;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateAutomatedMessage = createAsyncThunk<ChangeAutomatedMessageModel, string, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/ReactivateAutomatedMessage',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/reactivate?automatedMessageId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as ChangeAutomatedMessageModel;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteAutomatedMessage = createAsyncThunk<ChangeAutomatedMessageModel, string, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/DeleteAutomatedMessage',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/delete?automatedMessageId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as ChangeAutomatedMessageModel;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

/**
 * A tenant switching itself out of a message it receives, or back in. The tenant is never in
 * the payload - the API always takes the caller's own from the token.
 */
export const SetAutomatedMessageTenantOptOut = createAsyncThunk<ChangeAutomatedMessageModel, SetTenantOptOutRequest, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/SetAutomatedMessageTenantOptOut',
    async (request: SetTenantOptOutRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/setTenantOptOut', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const optOutResponse = await response.json() as ChangeAutomatedMessageModel;
            return optOutResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

/**
 * Feeds the recipient tree. One call gives the whole corporation with each employee's tenant,
 * which is exactly the three levels the picker shows.
 * <para>
 * Its own endpoint rather than api/employee/getAllActiveByCorporation: that one's mapper
 * resolves every employee against the caller's own tenant and dereferences the result without
 * a null check, so it throws a NullReferenceException for anybody employed in a different
 * tenant of the same corporation - exactly the people this picker exists to list.
 * </para>
 */
export const GetCorporationEmployeesForRecipients = createAsyncThunk<RecipientTreeSourceEmployee[], void, { rejectValue: RejectedValue }>(
    'Slice/AutomatedMessageSlice/GetCorporationEmployeesForRecipients',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/automatedmessage/getRecipientOptions', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RecipientTreeSourceEmployee[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: AutomatedMessageSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    reloadData: false,
    message: '',
    showNotification: false,
    listOfActiveAutomatedMessages: [],
    listOfInactiveAutomatedMessages: [],
    automatedMessage: null,
    corporationEmployees: []
}

export const AutomatedMessageSlice = createSlice({
    name: 'AutomatedMessageSlice',
    initialState: initialValue,
    reducers: {
        resetAutomatedMessageSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfActiveAutomatedMessages = [];
            state.listOfInactiveAutomatedMessages = [];
            state.automatedMessage = null;
            state.corporationEmployees = [];
        },
        resetAutomatedMessage: (state) => {
            state.automatedMessage = null;
            state.isModalLoading = false;
            state.reloadData = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
    },
    extraReducers(builder) {
        //GetActiveAutomatedMessages
        builder.addCase(GetActiveAutomatedMessages.fulfilled, (state, action: PayloadAction<AutomatedMessageModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfActiveAutomatedMessages = action.payload;
        })
            .addCase(GetActiveAutomatedMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveAutomatedMessages.pending, state => {
                state.isLoading = true;
            })
        //GetInactiveAutomatedMessages
        builder.addCase(GetInactiveAutomatedMessages.fulfilled, (state, action: PayloadAction<AutomatedMessageModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveAutomatedMessages = action.payload;
        })
            .addCase(GetInactiveAutomatedMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveAutomatedMessages.pending, state => {
                state.isLoading = true;
            })
        //GetAutomatedMessage
        builder.addCase(GetAutomatedMessage.fulfilled, (state, action: PayloadAction<AutomatedMessageModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.automatedMessage = action.payload;
        })
            .addCase(GetAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //CreateAutomatedMessage
        builder.addCase(CreateAutomatedMessage.fulfilled, (state, action) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The automated message is successfully created.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(CreateAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateAutomatedMessage
        builder.addCase(UpdateAutomatedMessage.fulfilled, (state, action) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The automated message is successfully updated.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(UpdateAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateAutomatedMessage
        builder.addCase(DeactivateAutomatedMessage.fulfilled, (state, action: PayloadAction<ChangeAutomatedMessageModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The automated message is successfully deactivated.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(DeactivateAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateAutomatedMessage
        builder.addCase(ReactivateAutomatedMessage.fulfilled, (state, action: PayloadAction<ChangeAutomatedMessageModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The automated message is successfully reactivated.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(ReactivateAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteAutomatedMessage
        builder.addCase(DeleteAutomatedMessage.fulfilled, (state, action: PayloadAction<ChangeAutomatedMessageModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The automated message is successfully deleted.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(DeleteAutomatedMessage.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteAutomatedMessage.pending, state => {
                state.isModalLoading = true;
            })
        //SetAutomatedMessageTenantOptOut
        builder.addCase(SetAutomatedMessageTenantOptOut.fulfilled, (state, action) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = action.meta.arg.isExcluded
                ? 'Your tenant no longer receives this message.'
                : 'Your tenant receives this message again.';
            state.showNotification = true;
            state.reloadData = true;
            if (state.automatedMessage !== null) {
                state.automatedMessage.isMyTenantExcluded = action.meta.arg.isExcluded;
            }
        })
            .addCase(SetAutomatedMessageTenantOptOut.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(SetAutomatedMessageTenantOptOut.pending, state => {
                state.isModalLoading = true;
            })
        //GetCorporationEmployeesForRecipients
        builder.addCase(GetCorporationEmployeesForRecipients.fulfilled, (state, action: PayloadAction<RecipientTreeSourceEmployee[]>) => {
            state.message = '';
            state.corporationEmployees = action.payload;
        })
            .addCase(GetCorporationEmployeesForRecipients.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
    },
});

export const { resetAutomatedMessage, resetAutomatedMessageSlice, resetStatusCode } = AutomatedMessageSlice.actions;

export default AutomatedMessageSlice.reducer;
